import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';

import { PrivatePayrollAuctionLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { ThemedInput } from '../../CreditOperationAndSocialSecurityCards/components/ThemedInput';
import * as S from './styles';

interface OTPProps {
    signData: any;
    setSignData: (data: any) => void;
    setSignerStep: (step: string) => void;
    onSendOTPCode: (token: string) => void;
}

const OTP_LENGTH = 6;

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const phoneMask = (value: string) => {
    const d = value.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return d.length ? `(${d}` : '';
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export const PrivatePayrollAuctionOTP = ({
    signData,
    setSignData,
    setSignerStep,
    onSendOTPCode,
}: OTPProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);

    // ── Derived from signData ────────────────────────
    const submissionMethod = signData?.signer_data?.authentication_submission_method;
    const email = signData?.signer_data?.email ?? null;
    const phoneObj = signData?.signer_data?.phone ?? null;
    const formattedPhone = phoneObj
        ? `(${phoneObj.area_code}) ${phoneObj.number.slice(0, 5)}-${phoneObj.number.slice(5)}`
        : null;

    const allowUpdateData   = signData?.allow_update_authentication_data ?? false;
    const allowUpdateMethod = signData?.allow_update_authentication_method ?? false;
    const canEdit = allowUpdateData || allowUpdateMethod;

    // ── OTP state ────────────────────────────────────
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isBlocked, setIsBlocked] = useState(true);
    const [remainingTime, setRemainingTime] = useState(60);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // ── Update-auth-data bottom sheet ────────────────
    const updateModal = useAnimatedModal();
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([loading]);

    // Draft fields — initialised from signData, updated on successful PATCH
    const initialPhone = phoneObj ? `${phoneObj.area_code}${phoneObj.number}` : '';
    const [prevPhone, setPrevPhone]   = useState(initialPhone);
    const [prevEmail, setPrevEmail]   = useState(email ?? '');
    const [prevMethod, setPrevMethod] = useState(submissionMethod ?? '');

    const [draftPhone,  setDraftPhone]  = useState(phoneMask(initialPhone));
    const [draftEmail,  setDraftEmail]  = useState(email ?? '');
    const [draftMethod, setDraftMethod] = useState(submissionMethod ?? '');

    // Form validity
    const draftPhoneDigits = draftPhone.replace(/\D/g, '');
    const isPhoneValid  = !phoneObj || draftPhoneDigits.length === 11;
    const isEmailValid  = !email    || emailRegex.test(draftEmail);
    const hasChanged    =
        (phoneObj  && draftPhoneDigits !== prevPhone)  ||
        (email     && draftEmail       !== prevEmail)  ||
        (submissionMethod && draftMethod !== prevMethod);
    const isUpdateFormReady = isPhoneValid && isEmailValid && !!hasChanged;

    // ── Countdown timer ──────────────────────────────
    useEffect(() => {
        if (!isBlocked) return;
        if (remainingTime <= 0) {
            setIsBlocked(false);
            return;
        }
        const timer = setTimeout(() => setRemainingTime((t) => t - 1), 1000);
        return () => clearTimeout(timer);
    }, [isBlocked, remainingTime]);

    const handleResend = useCallback(() => {
        if (isBlocked) return;
        setIsBlocked(true);
        setRemainingTime(90);
        onSendOTPCode(signData?.signer_token ?? '');
    }, [isBlocked, onSendOTPCode, signData]);

    // ── Update auth data ─────────────────────────────
    const handleUpdateAuthData = async () => {
        if (!isUpdateFormReady || loading) return;

        const data: Record<string, any> = {};

        if (phoneObj && allowUpdateData) {
            data.phone = {
                international_dial_code: '55',
                area_code: draftPhoneDigits.slice(0, 2),
                number: draftPhoneDigits.slice(2),
            };
        }
        if (email && allowUpdateData) {
            data.email = draftEmail;
        }
        if (allowUpdateMethod) {
            data.authentication_submission_method = draftMethod;
        }

        const config = { headers: { Authorization: signData?.signer_token ?? '' } };
        setLoading(true);
        try {
            await axios.patch('/signer/authentication/data', data, config);

            // Persist "previous" so the form can detect further changes if re-opened
            if (phoneObj && allowUpdateData) setPrevPhone(draftPhoneDigits);
            if (email && allowUpdateData)    setPrevEmail(draftEmail);
            if (allowUpdateMethod)           setPrevMethod(draftMethod);

            // Resend code to the updated destination, reset OTP
            onSendOTPCode(signData?.signer_token ?? '');
            setIsBlocked(true);
            setRemainingTime(90);
            setOtp(Array(OTP_LENGTH).fill(''));
            setHasError(false);
            updateModal.close();
        } catch {
            // Keep form open so the user can try again
        } finally {
            setLoading(false);
        }
    };

    // ── OTP fill helper ──────────────────────────────
    const fillOtp = useCallback((code: string) => {
        const digits = code.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!digits) return;
        const next = Array(OTP_LENGTH).fill('');
        digits.split('').forEach((d, i) => { next[i] = d; });
        setOtp(next);
        setHasError(false);
        focusBox(Math.min(digits.length, OTP_LENGTH - 1));
    }, []);

    // ── Web OTP API (Android Chrome) ─────────────────
    useEffect(() => {
        if (!('OTPCredential' in window)) return;

        const controller = new AbortController();

        (navigator.credentials as any)
            .get({ otp: { transport: ['sms'] }, signal: controller.signal })
            .then((credential: any) => {
                if (credential?.code) fillOtp(credential.code);
            })
            .catch(() => {});

        return () => controller.abort();
    }, [fillOtp]);

    // ── OTP input handling ───────────────────────────
    const focusBox = (index: number) => {
        const el = inputRefs.current[index];
        if (!el) return;
        el.focus();
        el.select();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw.length > 1) { fillOtp(raw); return; }

        const digit = raw.slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        setHasError(false);
        if (digit && index < OTP_LENGTH - 1) focusBox(index + 1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            if (otp[index]) {
                const next = [...otp]; next[index] = ''; setOtp(next);
            } else if (index > 0) {
                const next = [...otp]; next[index - 1] = ''; setOtp(next); focusBox(index - 1);
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault(); focusBox(index - 1);
        } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            e.preventDefault(); focusBox(index + 1);
        } else if (e.key === 'Delete') {
            e.preventDefault(); const next = [...otp]; next[index] = ''; setOtp(next);
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        fillOtp(e.clipboardData.getData('text'));
    };

    // ── Confirm ──────────────────────────────────────
    const isComplete = otp.every((d) => d !== '');

    const handleConfirm = async () => {
        if (!isComplete || loading) return;

        const config = { headers: { Authorization: signData?.signer_token ?? '' } };
        setLoading(true);
        try {
            const response = await axios.patch(
                '/signer/authentication',
                { authentication_code: otp.join('') },
                config,
            );
            setSignData({ ...signData, ...response.data });
            if (response.data?.next_step) setSignerStep(response.data.next_step);
        } catch {
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isComplete) handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComplete]);

    if (loading) return <PrivatePayrollAuctionLoading />;

    // Current display destination (reflects draftMethod after update)
    const displayDestination = draftMethod === 'email' ? (prevEmail || email) : formattedPhone;

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.Content>
                <S.Title $color={primary}>
                    Insira o código de{'\u00A0'}autenticação
                </S.Title>

                <S.Subtitle>
                    O código de autenticação foi enviado para{' '}
                    <strong>{displayDestination}</strong>.{' '}
                    {canEdit && (
                        <S.EditLink $color={primary} onClick={updateModal.open}>
                            Editar
                        </S.EditLink>
                    )}
                </S.Subtitle>

                <S.FormSection>
                    <S.InputLabel>Confirme o código para finalizar</S.InputLabel>

                    <S.OTPRow onPaste={handlePaste}>
                        {otp.map((digit, i) => (
                            <S.OTPBox
                                key={i}
                                ref={(el) => { inputRefs.current[i] = el; }}
                                value={digit}
                                $hasError={hasError}
                                maxLength={OTP_LENGTH}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                onChange={(e) => handleChange(e, i)}
                                onKeyDown={(e) => handleKeyDown(e, i)}
                                onFocus={(e) => e.target.select()}
                                onClick={(e) => (e.target as HTMLInputElement).select()}
                            />
                        ))}
                    </S.OTPRow>

                    {hasError && (
                        <S.ErrorText>O código não confere com o enviado.</S.ErrorText>
                    )}

                    <S.ResendWrapper>
                        <S.ResendText>
                            Se você não recebeu o código, verifique sua caixa de spam ou{' '}
                            <S.ResendLink
                                $color={primary}
                                $disabled={isBlocked}
                                onClick={handleResend}
                            >
                                clique aqui para reenviar.
                            </S.ResendLink>
                        </S.ResendText>

                        {isBlocked && (
                            <S.BlockedBadge>
                                <span>Bloqueado por <strong>{remainingTime}s</strong></span>
                            </S.BlockedBadge>
                        )}
                    </S.ResendWrapper>
                </S.FormSection>
                <div ref={contentEndRef} />
            </S.Content>

            <S.Footer>
                <S.ConfirmButton
                    $color={primary}
                    $disabled={!isComplete}
                    disabled={!isComplete}
                    onClick={() => { if (!hasScrolledToEnd) { scrollToBottom(); return; } handleConfirm(); }}
                >
                    Confirmar
                    <ChevronRightIcon width={20} height={20} />
                </S.ConfirmButton>
                <PoweredByQITech />
            </S.Footer>

            {updateModal.show && (
                <S.ModalOverlay
                    $closing={updateModal.isClosing}
                    onClick={updateModal.close}
                >
                    <S.ModalSheet
                        $closing={updateModal.isClosing}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <S.SheetHandle />
                        <S.SheetTitle $color={primary}>Atualize seus dados</S.SheetTitle>

                        {phoneObj && allowUpdateData && (
                            <ThemedInput
                                labelValue="Celular"
                                labelColor="#666"
                                fieldValue={draftPhone}
                                hasError={!isPhoneValid}
                                errorMessage=""
                                type="numeric"
                                onChange={(e) => setDraftPhone(phoneMask(e.target.value))}
                                onBlur={() => {}}
                            />
                        )}

                        {email && allowUpdateData && (
                            <ThemedInput
                                labelValue="Email"
                                labelColor="#666"
                                fieldValue={draftEmail}
                                hasError={!isEmailValid}
                                errorMessage=""
                                type="email"
                                onChange={(e) =>
                                    setDraftEmail(e.target.value.toLowerCase().trim())
                                }
                                onBlur={() => {}}
                            />
                        )}

                        {allowUpdateMethod && (
                            <S.RadioGroup>
                                <S.RadioGroupLabel>Método de autenticação</S.RadioGroupLabel>
                                <S.RadioOption>
                                    <S.RadioInput
                                        type="radio"
                                        style={{ accentColor: primary }}
                                        checked={draftMethod === 'email'}
                                        onChange={() => setDraftMethod('email')}
                                    />
                                    Email
                                </S.RadioOption>
                                {phoneObj && (
                                    <>
                                        <S.RadioOption>
                                            <S.RadioInput
                                                type="radio"
                                                style={{ accentColor: primary }}
                                                checked={draftMethod === 'sms'}
                                                onChange={() => setDraftMethod('sms')}
                                            />
                                            SMS
                                        </S.RadioOption>
                                        <S.RadioOption>
                                            <S.RadioInput
                                                type="radio"
                                                style={{ accentColor: primary }}
                                                checked={draftMethod === 'whatsapp'}
                                                onChange={() => setDraftMethod('whatsapp')}
                                            />
                                            WhatsApp
                                        </S.RadioOption>
                                    </>
                                )}
                            </S.RadioGroup>
                        )}

                        <S.SheetButtonRow>
                            <S.UpdateButton
                                $color={primary}
                                $disabled={!isUpdateFormReady}
                                disabled={!isUpdateFormReady}
                                onClick={handleUpdateAuthData}
                            >
                                Atualizar
                            </S.UpdateButton>
                            <S.CancelButton onClick={updateModal.close}>
                                Cancelar
                            </S.CancelButton>
                        </S.SheetButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};
